/**
 * DataDog RUM (Real User Monitoring) integration
 * Handles RUM initialization, user context management, and analytics
 */

import { datadogRum } from '@datadog/browser-rum';
import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum-core';
import { reactPlugin } from '@datadog/browser-rum-react';
import { logDebug, logError } from '@ptek/client-logger';
import {
  DATADOG_SITE,
  resolveValidDatadogEnvironment,
} from '@ptek/datadog-constants';
import { formatName, type UserInfo } from '@ptek/shared-auth-sdk';
import type { AmbientScribeContext, FeatureFlags } from '../types';
import {
  buildWidgetConfigFlags,
  type WidgetConfigFlags,
} from '../utils/buildStartSetup';

let rumInitialized = false;

export interface WidgetConfigSnapshot extends WidgetConfigFlags {
  brand?: string;
  customerId?: string;
  brandName?: string;
  featureFlags?: FeatureFlags;
  features: {
    documentContext: boolean;
    connectionRetry: boolean;
    customTemplates: boolean;
    transcriptEditing: boolean;
    platformTemplateTable: boolean;
  };
}

export const buildWidgetConfigSnapshot = (
  context: AmbientScribeContext | undefined,
  user: Pick<UserInfo, 'brand' | 'customerId'>,
  brandName?: string
): WidgetConfigSnapshot => {
  const features = context?.features ?? {};

  const snapshot: WidgetConfigSnapshot = {
    features: {
      documentContext: Boolean(features.documentContext),
      connectionRetry: Boolean(features.connectionRetry),
      customTemplates: Boolean(features.customTemplates),
      transcriptEditing: Boolean(features.transcriptEditing),
      platformTemplateTable: Boolean(features.platformTemplateTable),
    },
    ...buildWidgetConfigFlags(context),
  };

  if (Object.keys(features).length > 0) {
    snapshot.featureFlags = features;
  }
  if (user.brand) {
    snapshot.brand = user.brand;
  }
  if (user.customerId) {
    snapshot.customerId = String(user.customerId);
  }
  if (brandName) {
    snapshot.brandName = brandName;
  }

  return snapshot;
};

/**
 * Get widget-specific API URLs for resource filtering
 * These are the only URLs that should be tracked by RUM
 */
const getWidgetApiUrls = (): string[] => {
  const urls: string[] = [];

  const whisperWsUrl = import.meta.env.VITE_WHISPER_WS_URL;
  const whisperRestUrl = import.meta.env.VITE_WHISPER_REST_URL;
  const apiGatewayUrl = import.meta.env.VITE_API_GATEWAY_URL;

  if (whisperWsUrl) {
    const domain = whisperWsUrl.replace(/^wss?:\/\//, '').split('/')[0];
    urls.push(domain);
  }

  if (whisperRestUrl) {
    const domain = whisperRestUrl.replace(/^https?:\/\//, '').split('/')[0];
    urls.push(domain);
  }

  // This is pending migration to the new service--can be removed once migration is complete
  if (apiGatewayUrl) {
    const domain = apiGatewayUrl.replace(/^https?:\/\//, '').split('/')[0];
    urls.push(domain);
  }

  return urls;
};

const filterResourcesForWidgetUrlsOnly = (event: RumEvent) => {
  logDebug('[RUM DEBUG] Event:', JSON.stringify(event));
  if (event.type !== 'resource') {
    return true;
  }

  const resourceEvent = event as { resource?: { url?: string } };
  const resourceUrl = resourceEvent.resource?.url;

  // If we can't determine the URL, drop it to be safe
  if (!resourceUrl) {
    return false;
  }

  const widgetApiUrls = getWidgetApiUrls();

  const isWidgetResource = widgetApiUrls.some((apiUrl) =>
    resourceUrl.includes(apiUrl)
  );

  if (!isWidgetResource) {
    logDebug('[RUM DEBUG] Filtered out:', resourceUrl);
  } else {
    logDebug('[RUM DEBUG] Tracking:', resourceUrl);
  }

  return isWidgetResource;
};

const DATADOG_RUM_CONFIG: RumInitConfiguration = {
  applicationId: import.meta.env.VITE_DATADOG_AI_SCRIBE_RHEO_APP_ID,
  clientToken: import.meta.env.VITE_DATADOG_AI_SCRIBE_RHEO_CLIENT_TOKEN,
  site: DATADOG_SITE,
  service: 'ambient-scribe-widget',
  version: '1.0.2',
  env: resolveValidDatadogEnvironment(import.meta.env.VITE_DATADOG_ENV_TAG),
  sessionSampleRate: import.meta.env.VITE_DATADOG_SESSION_SAMPLE_RATE
    ? parseInt(import.meta.env.VITE_DATADOG_SESSION_SAMPLE_RATE, 10)
    : 100,
  trackResources: true,
  trackLongTasks: true,
  trackUserInteractions: true,
  // Session replay is off unless it is manually started (not implemented yet)
  startSessionReplayRecordingManually: true,
  traceSampleRate: 0,
  allowedTracingUrls: [window.location.origin] as string[],
  defaultPrivacyLevel: 'mask-user-input',
  plugins: [reactPlugin()],
  beforeSend: filterResourcesForWidgetUrlsOnly,
};

const shouldEnableDatadogRum = (): boolean => {
  if (!DATADOG_RUM_CONFIG.applicationId || !DATADOG_RUM_CONFIG.clientToken) {
    return false;
  }

  return import.meta.env.VITE_DATADOG_ENABLE_RUM === 'true';
};

export const initializeDatadogRum = (): boolean => {
  if (rumInitialized) {
    logDebug('RUM', 'RUM already initialized, skipping');
    return true;
  }

  if (!shouldEnableDatadogRum()) {
    logDebug('RUM', 'RUM is not enabled');
    return false;
  }

  try {
    datadogRum.init(DATADOG_RUM_CONFIG);
    rumInitialized = true;

    logDebug('RUM', 'RUM initialized successfully');
    return true;
  } catch (error) {
    rumInitialized = false;
    logError('RUM', 'Failed to initialize RUM:', error);
    return false;
  }
};

export const clearDatadogRumUserContext = (): void => {
  if (!rumInitialized) return;

  try {
    datadogRum.clearUser();
    logDebug('RUM', 'User context cleared');
  } catch (error) {
    logError('RUM', 'Failed to clear user context:', error);
  }
};

export const setDatadogRumUserContext = (userInfo: UserInfo): void => {
  if (!rumInitialized) return;

  try {
    const user: {
      id: string;
      name?: string;
      email?: string;
      customerId?: string;
      market?: string;
      brand?: string;
    } = {
      id: userInfo.userId || 'unknown-user',
    };

    if (userInfo.firstName || userInfo.lastName) {
      user.name = formatName(userInfo);
    }
    if (userInfo.email) {
      user.email = userInfo.email;
    }
    if (userInfo.customerId) {
      user.customerId = String(userInfo.customerId);
    }
    if (userInfo.market) {
      user.market = String(userInfo.market);
    }
    if (userInfo.brand) {
      user.brand = String(userInfo.brand);
    }

    datadogRum.setUser(user);

    logDebug('RUM', 'User context set for user:', user.id);
  } catch (error) {
    logError('RUM', 'Failed to set user context:', error);
  }
};

export const recordWidgetConfig = (snapshot: WidgetConfigSnapshot): void => {
  if (!rumInitialized) return;

  try {
    datadogRum.setGlobalContextProperty('widgetConfig', snapshot);

    const { featureFlags } = snapshot;
    if (featureFlags && Object.keys(featureFlags).length > 0) {
      datadogRum.setGlobalContextProperty('features', featureFlags);
      for (const [flagKey, value] of Object.entries(featureFlags)) {
        datadogRum.addFeatureFlagEvaluation(flagKey, value);
      }
    }

    datadogRum.addAction('widget_config_initialized');

    logDebug('RUM', 'Widget config recorded for brand:', snapshot.brand);
  } catch (error) {
    logError('RUM', 'Failed to record widget config:', error);
  }
};
